import { FormField } from '@folio/components/forms';
import { SearchInput } from '@folio/components/features';
import type { Field } from 'korint-sdk/react';
import _ from 'lodash';
import { useState } from 'react';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';
import { Group } from '@folio/components/ui/flex';

const years = Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(y => ({
  value: y.toString(),
  label: y.toString(),
}));

const months = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
].map((label, index) => ({
  value: _.padStart((index + 1).toString(), 2, '0'),
  label,
}));

const getDate = (year: string, month: string) => {
  if (!year || !month) return;
  return `${year}-${month}`;
};

interface FormMonthInputProps extends ControllerRenderProps {
  field: Field;
  min?: string;
  max?: string;
}

// Input type="month" has poor browser support (no Firefox support, nor Safari support)
export function FormMonthInput({
  field,
  value,
  min,
  max,
  disabled,
  onChange,
}: FormMonthInputProps) {
  const form = useFormContext();

  const [defaultYear, defaultMonth] = (value?.split('-') ?? []) as [
    string | undefined,
    string | undefined,
  ];

  const [year, setYear] = useState<string | undefined>(defaultYear);
  const [month, setMonth] = useState<string | undefined>(defaultMonth);

  const [yearSearch, setYearSearch] = useState('');
  const [monthSearch, setMonthSearch] = useState('');

  const minYear = min ? Number.parseInt(min.split('-')[0], 10) : undefined;
  const minMonth = min ? Number.parseInt(min.split('-')[1], 10) : undefined;
  const maxYear = max ? Number.parseInt(max.split('-')[0], 10) : undefined;
  const maxMonth = max ? Number.parseInt(max.split('-')[1], 10) : undefined;

  const filteredYears = years
    .filter(({ value }) => {
      const yearNum = Number.parseInt(value, 10);
      if (minYear !== undefined && yearNum < minYear) return false;
      if (maxYear !== undefined && yearNum > maxYear) return false;
      return true;
    })
    .filter(({ label }) => label.includes(yearSearch));

  const filteredMonths = months
    .filter(({ value }) => {
      if (!year) return true;
      const yearNum = Number.parseInt(year, 10);
      const monthNum = Number.parseInt(value, 10);

      if (minYear !== undefined && minMonth !== undefined && yearNum === minYear) {
        if (monthNum < minMonth) return false;
      }

      if (maxYear !== undefined && maxMonth !== undefined && yearNum === maxYear) {
        if (monthNum > maxMonth) return false;
      }

      return true;
    })
    .filter(({ label }) => label.toUpperCase().includes(monthSearch.toUpperCase()));

  const selectedYear = years.find(y => y.value === year) ?? null;
  const selectedMonth = months.find(m => m.value === month) ?? null;

  return (
    <FormField
      control={form.control}
      name={field.name}
      render={() => (
        <Group align="stretch">
          <SearchInput
            items={filteredYears}
            isLoading={false}
            disabled={disabled}
            itemKey="value"
            onSelect={({ value }) => {
              setYear(value);
              setYearSearch('');
              onChange(getDate(value, month ?? ''));
            }}
            placeholder="Année"
            search={yearSearch}
            renderItem={({ label }) => <div>{label}</div>}
            selected={selectedYear}
            setSearch={setYearSearch}
          />
          <SearchInput
            items={filteredMonths}
            isLoading={false}
            disabled={disabled || !year}
            itemKey="value"
            onSelect={({ value }) => {
              setMonth(value);
              setMonthSearch('');
              onChange(getDate(year ?? '', value));
            }}
            placeholder="Mois"
            search={monthSearch}
            renderItem={({ label }) => <div>{label}</div>}
            selected={selectedMonth}
            setSearch={setMonthSearch}
          />
        </Group>
      )}
    />
  );
}
